import type { CSSProperties, ReactNode } from 'react'

import {
  AddFilterButton,
  Card,
  DataTable,
  Filters,
  Search,
} from '@/components/ui'
import type { SelectOption } from '@/components/ui/filters/FilterDialog/inputs/types'
import type { TableData } from '@/components/ui/table/DataTable/types'
import type { ColumnDef } from '@/components/ui/table/types'
import {
  type FieldSchema,
  type Filter,
  FiltersProvider,
  useFilters,
  useSearchQuery,
} from '@/stores'

// What one list shows for the filters in force. The rows arrive already filtered and sorted.
export interface ListView<Row> {
  rows: Row[]
  // Changes whenever the rows do, so the table starts again on page 1.
  key: string
  totals: ReactNode
  groups: ReactNode
  // Optional chips under the toolbar, such as the types inside the chosen group.
  chips?: ReactNode
}

interface PortfolioTableProps<Row extends TableData> {
  // Own filter store per list, persisted by contextId, the same way the Transactions page owns one.
  contextId: string
  dialogId: string
  schema: FieldSchema[]
  getUniqueValues: (field: string) => SelectOption[]
  columns: ColumnDef<Row, unknown>[]
  emptyMessage: string
  view: (filters: Filter[], searchQuery: string) => ListView<Row>
  // The tab strip above the card. It gets the filtered rows for its Export button.
  renderStrip: (rows: Row[]) => ReactNode
}

const ROW_HEIGHT = 41
const ROWS_IN_VIEW = 15
const MIN_TABLE_HEIGHT = 2 * ROW_HEIGHT

// Tall enough for the rows up to a cap, so three rows do not sit in a 600px box.
const tableHeight = (rowCount: number) =>
  Math.max(
    MIN_TABLE_HEIGHT,
    (Math.min(rowCount, ROWS_IN_VIEW) + 1) * ROW_HEIGHT,
  )

const PortfolioTableInner = <Row extends TableData>({
  dialogId,
  schema,
  getUniqueValues,
  columns,
  emptyMessage,
  view,
  renderStrip,
}: Omit<PortfolioTableProps<Row>, 'contextId'>) => {
  const filters = useFilters()
  const searchQuery = useSearchQuery()
  const list = view(filters, searchQuery)
  const style = {
    '--portfolio-table-height': `${tableHeight(list.rows.length)}px`,
  } as CSSProperties

  return (
    <div className="flex flex-col gap-4">
      {renderStrip(list.rows)}
      <Card className="p-6 text-sm">
        {list.totals}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          {list.groups}
          <div className="flex items-center gap-2">
            <Search />
            <AddFilterButton dialogId={dialogId} schema={schema} />
          </div>
        </div>
        <div className="mt-3 empty:hidden">
          <Filters
            dialogId={dialogId}
            schema={schema}
            getUniqueValues={getUniqueValues}
          />
        </div>
        {list.chips}
        <div className="mt-4 flex flex-col" style={style}>
          <DataTable
            key={list.key}
            columns={columns}
            data={list.rows}
            height="h-(--portfolio-table-height)"
            emptyMessage={emptyMessage}
            enableRowSelection={false}
          />
        </div>
      </Card>
    </div>
  )
}

// A paged, virtualised list with the house Search, Add Filter and filter chips. The caller does
// the filtering, so its counts and totals always match the rows shown.
export const PortfolioTable = <Row extends TableData>({
  contextId,
  ...props
}: PortfolioTableProps<Row>) => (
  <FiltersProvider contextId={contextId}>
    <PortfolioTableInner {...props} />
  </FiltersProvider>
)
